import { useEffect, useState, useRef } from "react";
import { Users, Ticket, Mail, QrCode, Copy, Check, Download, BarChart3, Cake, UserPlus, TicketCheck, Timer, Hourglass } from "lucide-react";
import { Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { StatCard, Card, btnPrimary, btnGhost, downloadBlob, fmtDate } from "./shared";
import { CouponListDialog } from "./CouponListDialog";

const COUPON_CARDS = [
  { key: "tilgjengelig", label: "Tilgjengelige kuponger", icon: Ticket },
  { key: "aktiv", label: "Aktive kuponger", icon: Timer },
  { key: "innlost", label: "Innløste kuponger", icon: TicketCheck },
  { key: "utgatt", label: "Utgåtte kuponger", icon: Hourglass },
];

function CouponOverview({ data }) {
  const [open, setOpen] = useState(null);
  return (
    <Card className="mt-6" title="Kuponger" icon={Ticket} testid="coupon-overview">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {COUPON_CARDS.map(({ key, label, icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setOpen({ key, label })}
            className="rounded-2xl text-left transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]"
            data-testid={`coupon-stat-${key}-btn`}
          >
            <StatCard
              icon={icon}
              label={label}
              value={data ? data[key].totalt : "–"}
              sub={data ? `Velkomst ${data[key].velkomst} · Kampanje ${data[key].kampanje}` : null}
              testid={`coupon-stat-${key}`}
            />
          </button>
        ))}
      </div>
      <p className="mt-4 text-xs text-[#A8A29E]">
        Klikk på et tall for å se gjestene. Aktive = aktivert av gjesten og innenfor 30 minutter. Utgåtte = utløpt før aktivering, eller aktivert uten å bli innløst innen 30 minutter.
      </p>
      <CouponListDialog status={open?.key} label={open?.label} onClose={() => setOpen(null)} />
    </Card>
  );
}

export default function Oversikt() {
  const [stats, setStats] = useState({ total: 0, brukt: 0, markedsforing: 0, utlopt: 0, aldersgrupper: [] });
  const [birthdays, setBirthdays] = useState({ month_name: "", items: [] });
  const [copied, setCopied] = useState(false);
  const [recent, setRecent] = useState([]);
  const [coupons, setCoupons] = useState(null);
  const qrRef = useRef(null);
  const regUrl = `${window.location.origin}/gave`;

  useEffect(() => {
    api
      .get("/admin/registrations", { params: { q: "" } })
      .then((res) => setStats(res.data.stats))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));
    api.get("/admin/coupon-stats").then((res) => setCoupons(res.data)).catch(() => {});
    api.get("/admin/birthdays").then((res) => setBirthdays(res.data)).catch(() => {});
    api.get("/admin/customers", { params: { q: "" } }).then((res) => setRecent(res.data.items.slice(0, 8))).catch(() => {});
  }, []);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(regUrl);
    } catch {
      /* ignore */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadQr = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = "demo-qr.png";
    a.click();
  };

  const exportBirthdays = async () => {
    try {
      const res = await api.get("/admin/export-birthdays", { responseType: "blob" });
      downloadBlob(res.data, `bursdager_${birthdays.month_name || "maned"}.csv`);
      toast.success("Bursdagsliste eksportert");
    } catch {
      toast.error("Kunne ikke eksportere bursdager");
    }
  };

  const groups = stats.aldersgrupper || [];
  const max = Math.max(1, ...groups.map((g) => g.antall));

  return (
    <div data-testid="oversikt-page">
      <h1 className="font-serif-display text-3xl font-600 text-[#FDFBF7]">Oversikt</h1>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <StatCard icon={Users} label="Registreringer" value={stats.total} testid="stat-total" />
        <StatCard icon={Mail} label="Markedsføringssamtykke" value={stats.markedsforing} testid="stat-marketing" />
      </div>

      <CouponOverview data={coupons} />

      <div className="mt-6 flex flex-col gap-6 rounded-2xl border border-white/10 bg-[#282421] p-6 sm:flex-row sm:items-center">
        <div ref={qrRef} className="flex-shrink-0 rounded-xl bg-white p-3" data-testid="qr-image">
          <QRCodeCanvas value={regUrl} size={176} level="M" includeMargin={false} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 text-[#D4AF37]">
            <QrCode className="h-5 w-5" />
            <h2 className="font-semibold text-[#FDFBF7]">QR-kode &amp; registreringslenke</h2>
          </div>
          <p className="mt-2 text-sm text-[#A8A29E]">
            Bruk denne lenken på plakater og bordkort. Gjesten skanner QR-koden og kommer direkte til registreringssiden.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <code className="rounded-lg border border-white/10 bg-[#1A1715] px-4 py-2.5 text-sm text-[#D4AF37]" data-testid="registration-url">
              {regUrl}
            </code>
            <button onClick={copyUrl} data-testid="copy-url-btn" className={btnPrimary}>
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? "Kopiert" : "Kopier"}
            </button>
            <button onClick={downloadQr} data-testid="download-qr-btn" className={btnGhost}>
              <Download className="h-4 w-4" /> Last ned QR
            </button>
          </div>
        </div>
      </div>

      <Card
        className="mt-6"
        title="Siste registreringer"
        icon={UserPlus}
        testid="recent-registrations"
        action={<Link to="/admin/dashboard/kunder" className="text-xs text-[#D4AF37] hover:underline" data-testid="see-all-customers">Se alle →</Link>}
      >
        {recent.length === 0 ? (
          <p className="text-sm text-[#A8A29E]">Ingen registreringer ennå.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wide text-[#A8A29E]">
                  <th className="pb-2 pr-4">Navn</th>
                  <th className="pb-2 pr-4">E-post</th>
                  <th className="pb-2 pr-4">Telefon</th>
                  <th className="pb-2 pr-4">Fødselsdato</th>
                  <th className="pb-2">Registrert (dato og kl.)</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((c) => (
                  <tr key={c.id} className="border-t border-white/5" data-testid={`recent-row-${c.id}`}>
                    <td className="py-2.5 pr-4 font-medium text-[#FDFBF7]">{c.fornavn}</td>
                    <td className="py-2.5 pr-4 text-[#A8A29E]">{c.epost}</td>
                    <td className="py-2.5 pr-4 text-[#A8A29E]">{c.telefon}</td>
                    <td className="py-2.5 pr-4 text-[#A8A29E]">{c.fodselsdato || "—"}</td>
                    <td className="py-2.5 text-[#E5C564]">{fmtDate(c.opprettet)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title="Aldersgrupper" icon={BarChart3} testid="age-stats">
          <div className="space-y-3">
            {groups.every((g) => g.antall === 0) ? (
              <p className="text-sm text-[#A8A29E]">Ingen data ennå.</p>
            ) : (
              groups.map((g) => (
                <div key={g.gruppe} className="flex items-center gap-3" data-testid={`age-group-${g.gruppe}`}>
                  <span className="w-20 flex-shrink-0 text-xs text-[#A8A29E]">{g.gruppe}</span>
                  <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-[#1A1715]">
                    <div className="h-full rounded-full bg-[#D4AF37] transition-[width] duration-500" style={{ width: `${(g.antall / max) * 100}%` }} />
                  </div>
                  <span className="w-8 flex-shrink-0 text-right text-sm font-semibold text-[#FDFBF7]">{g.antall}</span>
                </div>
              ))
            )}
          </div>
        </Card>

        <Card
          title={`Bursdager i ${birthdays.month_name || "denne måneden"}`}
          icon={Cake}
          testid="birthday-section"
          action={
            <button onClick={exportBirthdays} disabled={!birthdays.items.length} data-testid="export-birthdays-btn" className={`${btnPrimary} !px-3 !py-2 !text-xs`}>
              <Download className="h-3.5 w-3.5" /> CSV
            </button>
          }
        >
          {birthdays.items.length === 0 ? (
            <p className="text-sm text-[#A8A29E]" data-testid="birthday-empty">Ingen bursdager blant samtykkede gjester denne måneden.</p>
          ) : (
            <ul className="max-h-56 space-y-2 overflow-y-auto pr-1">
              {birthdays.items.map((b) => (
                <li key={b.id} className="flex items-center justify-between rounded-xl border border-white/5 bg-[#1A1715] px-3 py-2.5" data-testid={`birthday-row-${b.id}`}>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-[#FDFBF7]">{b.fornavn}</p>
                    <p className="truncate text-xs text-[#A8A29E]">{b.epost}</p>
                  </div>
                  <p className="ml-3 flex-shrink-0 text-xs text-[#E5C564]">{b.fodselsdato}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
